// Per-worker actions, moved off the deleted Upstream pool table.
//
// Start/stop act on the container and only exist for workers this manager
// deployed; poll, edit and remove apply to every worker. Redeploy is the
// opposite pair to start/stop: it acts on a worker's own machine over SSH, so it
// only exists where a saved key makes that possible.

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@veil/ui/components/button";
import {
	ResponsiveDialog,
	ResponsiveDialogAction,
	ResponsiveDialogCancel,
	ResponsiveDialogContent,
	ResponsiveDialogDescription,
	ResponsiveDialogFooter,
	ResponsiveDialogHeader,
	ResponsiveDialogTitle,
} from "@veil/ui/components/responsive-dialog";
import { cn } from "@veil/ui/lib/utils";
import Pencil from "lucide-react/dist/esm/icons/pencil";
import Play from "lucide-react/dist/esm/icons/play";
import RefreshCw from "lucide-react/dist/esm/icons/refresh-cw";
import ServerCog from "lucide-react/dist/esm/icons/server-cog";
import Square from "lucide-react/dist/esm/icons/square";
import Trash2 from "lucide-react/dist/esm/icons/trash-2";
import { useState } from "react";
import { toast } from "sonner";
import { useLocation } from "wouter";
import { api, unwrap } from "@/lib/api";
import type { WorkerRow } from "@/lib/workers/types";
import {
	canReachRemoteHost,
	useWorkerRedeploy,
	useWorkerStart,
	useWorkerStop,
} from "@/lib/workers/use-worker-lifecycle";

export function WorkerRowActions({
	worker,
	onEdit,
	onRemove,
}: {
	worker: WorkerRow;
	onEdit: () => void;
	onRemove: () => void;
}) {
	const queryClient = useQueryClient();
	const [, navigate] = useLocation();
	const [confirmingRedeploy, setConfirmingRedeploy] = useState(false);
	const followOperation = (operationId: string) =>
		navigate(`/operations/${operationId}`);
	const stop = useWorkerStop({ onStarted: followOperation });
	const start = useWorkerStart({ onStarted: followOperation });
	const redeploy = useWorkerRedeploy({ onStarted: followOperation });
	const stopped = worker.desiredState === "stopped";
	const lifecyclePending = stop.isPending || start.isPending;
	const reachable = canReachRemoteHost(worker);

	const pollNow = useMutation({
		mutationFn: () => unwrap(api.api.workers["poll-now"].post()),
		onSuccess: () => {
			toast.success(`Polling ${worker.hostname}…`);
			queryClient.invalidateQueries({ queryKey: ["workers"] });
		},
		onError: (err) =>
			toast.error(err instanceof Error ? err.message : "Poll failed"),
	});

	return (
		<div className="flex items-center justify-end gap-1">
			{worker.managed ? (
				<Button
					variant="ghost"
					size="icon-sm"
					onClick={() =>
						stopped
							? start.mutate(worker.hostname)
							: stop.mutate(worker.hostname)
					}
					disabled={lifecyclePending}
					title={
						stopped
							? "Start this worker's container"
							: "Drain and stop this worker's container"
					}
					aria-label={`${stopped ? "Start" : "Stop"} ${worker.hostname}`}
				>
					{stopped ? <Play /> : <Square />}
				</Button>
			) : null}
			{reachable ? (
				<Button
					variant="ghost"
					size="icon-sm"
					onClick={() => setConfirmingRedeploy(true)}
					disabled={redeploy.isPending}
					title={`Redeploy this worker on ${worker.publicIp} over SSH`}
					aria-label={`Redeploy ${worker.hostname}`}
				>
					<ServerCog />
				</Button>
			) : null}
			<Button
				variant="ghost"
				size="icon-sm"
				onClick={() => pollNow.mutate()}
				disabled={pollNow.isPending || stopped}
				title={
					stopped
						? "Worker is stopped — start it to resume polling"
						: "Poll this worker now"
				}
				aria-label={`Poll ${worker.hostname} now`}
			>
				<RefreshCw className={cn(pollNow.isPending && "animate-spin")} />
			</Button>
			<Button
				variant="ghost"
				size="icon-sm"
				onClick={onEdit}
				title="Edit this worker's load-balancer config"
				aria-label={`Edit ${worker.hostname}`}
			>
				<Pencil />
			</Button>
			<Button
				variant="ghost"
				size="icon-sm"
				onClick={onRemove}
				title="Remove worker"
				aria-label={`Remove ${worker.hostname}`}
			>
				<Trash2 />
			</Button>

			<RedeployWorkerDialog
				worker={worker}
				open={confirmingRedeploy}
				onOpenChange={setConfirmingRedeploy}
				pending={redeploy.isPending}
				onConfirm={() => {
					setConfirmingRedeploy(false);
					redeploy.mutate(worker.hostname);
				}}
			/>
		</div>
	);
}

/**
 * Confirm, because a redeploy restarts a running worker.
 *
 * It is not destructive — the install volume and everything in it survives — but
 * the stack goes down and back up on someone else's machine, so it gets the same
 * confirm treatment as a removal rather than firing off an icon click.
 */
function RedeployWorkerDialog({
	worker,
	open,
	onOpenChange,
	pending,
	onConfirm,
}: {
	worker: WorkerRow;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	pending: boolean;
	onConfirm: () => void;
}) {
	const tag = imageTagOf(worker.imageRef);

	return (
		<ResponsiveDialog variant="alert" open={open} onOpenChange={onOpenChange}>
			<ResponsiveDialogContent>
				<ResponsiveDialogHeader>
					<ResponsiveDialogTitle>
						Redeploy <span className="font-mono">{worker.hostname}</span>?
					</ResponsiveDialogTitle>
					<ResponsiveDialogDescription>
						This opens <span className="font-mono">{worker.publicIp}</span> with
						the worker's saved key and runs the whole deploy again{" "}
						{tag ? (
							<>
								on <span className="font-mono">{tag}</span>, the tag it is
								already pinned to
							</>
						) : (
							"on this manager's configured image tag"
						)}
						. Its containers restart, so any browser session open on it ends.
						The install volume is untouched, so the downloaded browser builds
						and profile bundles stay. This is also how a deploy that failed
						part-way gets finished.
					</ResponsiveDialogDescription>
				</ResponsiveDialogHeader>
				<ResponsiveDialogFooter>
					<ResponsiveDialogCancel disabled={pending}>
						Cancel
					</ResponsiveDialogCancel>
					<ResponsiveDialogAction
						disabled={pending}
						onClick={(e) => {
							e.preventDefault();
							onConfirm();
						}}
					>
						{pending ? "Starting…" : "Redeploy"}
					</ResponsiveDialogAction>
				</ResponsiveDialogFooter>
			</ResponsiveDialogContent>
		</ResponsiveDialog>
	);
}

/**
 * The tag a worker is pinned to, matching `imageTagFromRef` on the server so the
 * dialog names the tag the redeploy will actually use.
 */
function imageTagOf(imageRef: string | null): string | null {
	if (!imageRef) return null;
	const cut = imageRef.lastIndexOf(":");
	if (cut < 0) return null;
	const tag = imageRef.slice(cut + 1);
	if (tag.includes("/") || imageRef.includes("@")) return null;
	return tag || null;
}
